import { Button } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AppDispatch, useSelector } from '../../../../../store/AppStore';
import ConfirmDialog from '../../../../../_components/confirm-dialog/ConfirmDialog';
import { useServiceOpsForm, serviceOpsFormParam } from '../../../../../hooks';
import { IServiceOperationValidationModel } from '../../../../../models';
import { deleteAllEgressEventMapping } from '../../../../../store/reducer/services/EgressEventMappingReducer';
import { deleteIngressEventMapping } from '../../../../../store/reducer/services/IngressEventMappingReducer';
import { clearServiceOperationMonitor } from '../../../../../store/reducer/services/ServiceOperationMonitorReducer';
import { resetServiceOperationFormOnCancelClick } from '../../../../../utilities/services/ServiceDetailsUtils';
import ServiceEventAssociationWrapper from '../event-association/service-event-association-wrapper/ServiceEventAssociationWrapper';
import classes from './OperationDetailsWrapper.module.scss';
import ServiceOperationFormWrapper from './operation-details-form/ServiceOperationFormWrapper';

interface IServiceOpsWrapperProps {
  isSaveClicked: boolean;
  onSaveActionHandler: () => void;
  onServiceOpsAdded: () => void;
  onCancelClicked: () => void;
  onServiceOperationEnteredOnUi: (serviceOperationName: string) => void;
  isEditButtonClick: boolean;
  editViewIndex: number;
  isAddNewServiceOperationClick: boolean;
  isViewButtonClick: boolean;
  viewMode: boolean;
  serviceEdit: boolean;
}

const OperationDetailsWrapper = ({
  isSaveClicked,
  onSaveActionHandler,
  onServiceOpsAdded,
  onServiceOperationEnteredOnUi,
  isEditButtonClick,
  editViewIndex,
  onCancelClicked,
  isAddNewServiceOperationClick,
  isViewButtonClick,
  viewMode,
  serviceEdit
}: IServiceOpsWrapperProps) => {
  /* Local state */
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaveValid, setIsSaveValid] = useState(false);
  const [eventFormVisibility, setEventFormVisibility] = useState(false);
  const [showOpsForm, setShowOpsForm] = useState(true);
  const [serviceOperationName, setServiceOperationName] = useState('');
  const [serviceOperationFormData, setServiceOperationFormData] = useState<IServiceOperationValidationModel>();
  const [operationUrl, setOperationUrl] = useState('');
  const [operationVerb, setOperationVerb] = useState('');
  const [isConfirm, setIsConfirm] = useState(false);
  /* Get data from redux store */
  const { serviceOperations } = useSelector(
    (state) => state.serviceOperationReducer
  );
  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );
  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );
  const { setFormParamValues } = useServiceOpsForm(serviceOperations);
  const isViewOrEdit = serviceEdit || viewMode;
  const isEditOrCreate = !viewMode;
  /* Hook */
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const onSaveValid = (value: boolean) => {
    setIsSaveValid(value);
  };

  const onServiceOperationEntered = (
    serviceOperationName: string,
    operationURL: string,
    operationVerb: string,
    serviceOperationFormData: IServiceOperationValidationModel
  ) => {
    setServiceOperationName(serviceOperationName);
    setServiceOperationFormData(serviceOperationFormData);
    onServiceOperationEnteredOnUi(serviceOperationName);
    setOperationUrl(operationURL);
    setOperationVerb(operationVerb);
  };

  const onSaveButtonClick = () => {
    onSaveActionHandler();
  };

  /**
   * Displays or hide the service operation form
   */
  const onServiceOpsFormClick = () => {
    setShowOpsForm(!showOpsForm);
    if (isViewOrEdit) setEventFormVisibility(false);
  };

  const onCancelAddServiceOperation = () => {
    if (!viewMode) {
      setShowConfirmDialog(true);
      return;
    }
    onCancelClicked();
  };

  /**
   * @method showEventForm
   * @description Shows event form and hides the service operation form
   */
  const showEventForm = () => {
    setEventFormVisibility(!eventFormVisibility);
    if (isViewOrEdit) setShowOpsForm(false);
  };

  const deleteIngressEgressEvents = () => {
    const serviceOperationArray = serviceOperations.map(
      (item) => item.serviceOperation
    );
    const serviceOperationArrayFromIngressEvent = ingressEventMappingList.map(
      (item) => item.serviceOperationName
    );
    const ingressEventToBeDeleted = serviceOperationArrayFromIngressEvent.filter(
      (so1) => !serviceOperationArray.some((so2) => so1 === so2)
    );

    // delete the ingress/egress event if for the event, service operation does not exist
    // this covers the following scenario
    // if user clicks on add service operation and adds the ingress/egress event and cancels the adding service operation then related ingress
    // event must be deleted
    if (ingressEventToBeDeleted.length) {
      dispatch(
        deleteIngressEventMapping({
          serviceOperationName: ingressEventToBeDeleted[0]
        })
      );
    }
    // delete associated egress event
    const serviceOperationArrayFromEgressEvent = egressEventMappingList.map(
      (item) => item.serviceOperationName
    );
    const egressEventToBeDeleted = serviceOperationArrayFromEgressEvent.filter(
      (so1) => !serviceOperationArray.some((so2) => so1 === so2)
    );
    egressEventToBeDeleted.forEach((item) => {
      dispatch(deleteAllEgressEventMapping({ serviceOperationName: item }));
    });
  };

  const confirmCancelAddServiceOperation = () => {
    setIsConfirm(true);
    deleteIngressEgressEvents();
    dispatch(clearServiceOperationMonitor());
  };

  useEffect(() => {
    if (isEditButtonClick) {
      setServiceOperationName(serviceOperations[editViewIndex].serviceOperation);
    }
  }, [isEditButtonClick]);

  useEffect(() => {
    if (isConfirm) {
      setFormParamValues(resetServiceOperationFormOnCancelClick(serviceOpsFormParam));
      onCancelClicked();
    }
  }, [isConfirm]);

  useEffect(() => {
    deleteIngressEgressEvents();
  }, [serviceOperationName]);

  useEffect(() => {
    if (isViewButtonClick && serviceOperations.length > 0) {
      setServiceOperationName(serviceOperations[editViewIndex].serviceOperation);
    }
  }, [isViewButtonClick, serviceOperations]);

  return (
    <div className={classes['add-service-ops-wrapper']}>
      <ConfirmDialog
        label={translate('service_wrapper.msg_cancel_service_ops')}
        show={showConfirmDialog}
        onHide={() => {
          setShowConfirmDialog(false);
        }}
        btnContinueText={translate('service_wrapper.btn_cancel') ?? ''}
        btnCancelText={translate('service_wrapper.btn_dont_cancel') ?? ''}
        onConfirm={confirmCancelAddServiceOperation}
      />
      <Row>
        <ServiceOperationFormWrapper
          isSaveClicked={isSaveClicked}
          onServiceOpsAdded={onServiceOpsAdded}
          onServiceOperationEntered={onServiceOperationEntered}
          onSaveValid={onSaveValid}
          isEditButtonClick={isEditButtonClick}
          editViewIndex={editViewIndex}
          isAddNewServiceOperationClick={isAddNewServiceOperationClick}
          showOpsForm={showOpsForm}
          showForms={onServiceOpsFormClick}
          viewMode={viewMode}
          serviceEdit={serviceEdit}
          isViewButtonClick={isViewButtonClick}
        />
      </Row>
      <Row>
        <ServiceEventAssociationWrapper
          serviceOperationName={serviceOperationName}
          serviceOperationData={serviceOperationFormData}
          operationUrl={operationUrl}
          operationVerb={operationVerb}
          isView={isViewButtonClick}
          showForms={showEventForm}
          showEventForm={eventFormVisibility}
        />
      </Row>
      <Row className={classes['add-service-ops-btns']}>
        <Col sm={12} className="ci-button-group">
          <Button
            variant="secondary"
            onClick={onCancelAddServiceOperation}
            data-testid="btn-cancel-so"
          >
            {isEditOrCreate
              ? translate('add_service_operation_wrapper.btn_cancel')
              : translate('service_operation_wrapper.btn_back')}
          </Button>
          <Button
            className={classes['btn-save']}
            variant="primary"
            onClick={onSaveButtonClick}
            disabled={isSaveValid || viewMode}
            data-testid="btn-save-so"
          >
            {translate('add_service_operation_wrapper.btn_save_service_ops')}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default OperationDetailsWrapper;
